/** Combat's line shots (split from combat.ts): Lightning's chain from raider to raider and the Ballista's pierce along its line. */
import { TowerArchetype } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import { distanceSquared } from "../../world/roadGeometry";
import { LevelSim } from "../levelSim";
import { mythicValue } from "../mythics";
import { BASE_TIERS } from "../branches/ascension";
import { chainFalloffOf, chainTargetsOf, pierceCountOf, pierceWidthOf } from "../branches/shots";
import { applyHit, canTarget } from "./combat";

/** Lightning: after the primary hit, jump to the nearest untouched enemy within chain range, up to chainTargets hits. */
export function fireChain(sim: LevelSim, t: Tower, a: TowerArchetype, first: Enemy, damage: Fix64): void {
  const r2 = a.chainRange.mul(a.chainRange);
  let current = first;
  let hopDamage = damage;
  const visited = [first.id];
  sim.areaDepth++;
  // Mythic "Forked Bolt": more hops.
  const targets = (t.tier > BASE_TIERS ? chainTargetsOf(sim, t, a) : a.chainTargets) + mythicValue(sim, t.archetype, sim.mythics.lightningChain).floorToInt();
  const falloff = t.tier > BASE_TIERS ? chainFalloffOf(sim, t, a) : a.chainFalloff;
  for (let hop = 1; hop < targets; hop++) {
    hopDamage = hopDamage.mul(falloff);
    let next: Enemy | null = null;
    let bestD2 = Fix64.Zero;
    for (const e of sim.enemySlots) {
      if (!e.alive || !canTarget(a, e) || e.burrowed || visited.includes(e.id)) continue;
      const d2 = distanceSquared(current.x, current.y, e.x, e.y);
      if (d2.gt(r2)) continue;
      if (next === null || d2.lt(bestD2) || (d2.eq(bestD2) && e.id < next.id)) { next = e; bestD2 = d2; }
    }
    if (next === null) break;
    visited.push(next.id);
    sim.events.add(SimEventKind.ChainHit, next.id, t.id, hop);
    applyHit(sim, next, t, a, hopDamage);
    current = next;
  }
  sim.areaDepth--;
}

/** Ballista: hits up to pierceCount enemies within pierceWidth of the tower→target line, nearest first. */
export function firePierce(sim: LevelSim, t: Tower, a: TowerArchetype, target: Enemy, damage: Fix64): void {
  const tx = Fix64.fromInt(t.cell.x), ty = Fix64.fromInt(t.cell.y);
  const dx = target.x.sub(tx), dy = target.y.sub(ty);
  const len2 = dx.mul(dx).add(dy.mul(dy));
  if (len2.isZero()) { applyHit(sim, target, t, a, damage); return; }
  const range = sim.rangeOf(t);
  const range2 = range.mul(range);
  const width = t.tier > BASE_TIERS ? pierceWidthOf(sim, t, a) : a.pierceWidth;
  const width2 = width.mul(width);
  const hit: number[] = [];
  // Mythic "Skewer": more enemies per bolt.
  const count = (t.tier > BASE_TIERS ? pierceCountOf(sim, t, a) : a.pierceCount) + mythicValue(sim, t.archetype, sim.mythics.ballistaPierce).floorToInt();
  while (hit.length < count) {
    let best: Enemy | null = null;
    let bestProj = Fix64.Zero;
    for (const e of sim.enemySlots) {
      if (!e.alive || !canTarget(a, e) || e.burrowed || hit.includes(e.id)) continue;
      const ex = e.x.sub(tx), ey = e.y.sub(ty);
      const dot = ex.mul(dx).add(ey.mul(dy));
      if (dot.isNegative()) continue;
      const e2 = ex.mul(ex).add(ey.mul(ey));
      if (e2.gt(range2)) continue;
      const perp2 = e2.sub(dot.mul(dot).div(len2));
      if (perp2.gt(width2)) continue;
      if (best === null || dot.lt(bestProj) || (dot.eq(bestProj) && e.id < best.id)) { best = e; bestProj = dot; }
    }
    if (best === null) return;
    hit.push(best.id);
    applyHit(sim, best, t, a, damage);
  }
}
